import { isEqual } from "lodash-es";
import { overlay } from "overlay-kit";
import { type FC, type MouseEvent, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Trans } from "react-i18next";

import { SectionRole } from "../../shared/types/roles";

import { roleIcon } from "components/materials/home/roles";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { i18n, t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { Dialog } from "mds/components/Dialog";
import { ListItem } from "mds/components/ListItem";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { LoadingDotsIcon, PersonSingleIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store";
import {
  sectionSelectors,
  sectionUserSelectors,
  selectSectionsByTermAndCourse,
  userSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationError, toastSuccessMessage } from "utils/alerts";
import { isTemplateSection } from "utils/publishing";
import { SECTION_ROLES, getSectionResourceRole } from "utils/section";
import { getUserFullName } from "utils/user-utils";

type EnrollmentRoles = Record<string, SectionRole>;

type EnrollmentFormDataType = {
  courseId: string;
  termId: string;
  enrollments: EnrollmentRoles;
};

type RoleListboxOption = ListboxOption & { id: SectionRole };

const getRoleListboxItems = (): RoleListboxOption[] =>
  SECTION_ROLES.map((role) => ({
    id: role,
    title: t(`roles.section_${getSectionResourceRole(role)}`),
    iconStart: roleIcon[role] || <PersonSingleIcon />,
  }));

const buildEnrollmentsFromSectionUsers = (
  sectionUsers: { section_id: string; role: SectionRole; is_disabled: boolean }[],
): EnrollmentRoles =>
  Object.fromEntries(
    sectionUsers.filter((cu) => !cu.is_disabled).map((cu) => [cu.section_id, cu.role]),
  );

const updateEnrollments = async (
  userId: string,
  toEnable: { id: string; role: SectionRole }[],
  toDisable: string[],
  toAdd: { sectionId: string; role: SectionRole }[],
  toUpdateRole: { id: string; role: SectionRole }[],
): Promise<boolean> => {
  const createPromises = toAdd.map(({ sectionId, role }) =>
    storeApi.section_users.create(
      {
        section_id: sectionId,
        user_id: userId,
        role,
      },
      { skipToast: true },
    ),
  );
  const enablePromises = toEnable.map(({ id, role }) =>
    storeApi.section_users.partial_update({ id, is_disabled: false, role }, { skipToast: true }),
  );
  const disablePromises = toDisable.map((sectionUserId) =>
    storeApi.section_users.partial_update(
      { id: sectionUserId, is_disabled: true },
      { skipToast: true },
    ),
  );
  const roleUpdatePromises = toUpdateRole.map(({ id, role }) =>
    storeApi.section_users.partial_update({ id, role }, { skipToast: true }),
  );

  try {
    await Promise.all([
      ...createPromises,
      ...enablePromises,
      ...disablePromises,
      ...roleUpdatePromises,
    ]);
    toastSuccessMessage(t("user_options_menu.manage_enrollment.update_enrollment_success"));
    return true;
  } catch {
    toastLocalizedOperationError("update_enrollment_failure");
    return false;
  }
};

const BoldSectionNameList: FC<{ names: string[] }> = ({ names }) => {
  const parts = new Intl.ListFormat(i18n.language, {
    style: "long",
    type: "conjunction",
  }).formatToParts(names);

  return (
    <>
      {parts.map((part, index) =>
        part.type === "element" ? (
          // eslint-disable-next-line react/no-array-index-key
          <strong key={index}>{part.value}</strong>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part.value}</span>
        ),
      )}
    </>
  );
};

const confirmUnenrollment = (userName: string, sectionNames: string[]) => {
  let succeeded = false;

  return overlay.openAsync<boolean>(({ isOpen, close }) => (
    <ConfirmationDialog
      cancelButtonText={t("user_options_menu.manage_enrollment.go_back")}
      confirmButtonKind="destructive"
      confirmButtonText={t("user_options_menu.manage_enrollment.confirm_removal")}
      open={isOpen}
      title={t("user_options_menu.manage_enrollment.confirm_unenrollment")}
      onClose={() => close(succeeded)}
      onConfirm={() => {
        succeeded = true;
      }}
    >
      <Trans
        components={{ sections: <BoldSectionNameList names={sectionNames} /> }}
        count={sectionNames.length}
        i18nKey="user_options_menu.manage_enrollment.confirmation_dialog_body"
        t={t}
        values={{ userName }}
      />
    </ConfirmationDialog>
  ));
};

type ShowManageEnrollmentModalOptions = {
  userId: string;
};

// eslint-disable-next-line react-refresh/only-export-components
export const showManageEnrollmentModal = ({ userId }: ShowManageEnrollmentModalOptions) =>
  overlay.open(({ isOpen, unmount }) => (
    <ManageEnrollmentModal open={isOpen} userId={userId} onClose={unmount} />
  ));

type ManageEnrollmentModalProps = {
  open: boolean;
  userId: string;
  onClose: () => void;
};

export const ManageEnrollmentModal: FC<ManageEnrollmentModalProps> = ({
  open,
  userId,
  onClose,
}) => {
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));
  const sectionUsers = useListSelector((s) => sectionUserSelectors.filterByUserId(s, userId));
  const enabledSectionUsers = sectionUsers.filter((cu) => !cu.is_disabled);
  const initialEnrollments = buildEnrollmentsFromSectionUsers(sectionUsers);
  const enrolledCount = enabledSectionUsers.length;
  const sectionsById = useAppSelector(sectionSelectors.selectEntities);

  const [confirming, setConfirming] = useState(false);
  const {
    formState: { isSubmitting },
    handleSubmit,
    setValue,
    watch,
  } = useForm<EnrollmentFormDataType>({
    defaultValues: {
      courseId: "",
      termId: "",
      enrollments: initialEnrollments,
    },
  });

  const courseId = watch("courseId");
  const termId = watch("termId");
  const enrollments = watch("enrollments");

  const allSections = useAppSelector((s) =>
    selectSectionsByTermAndCourse(s, termId || null, courseId || null),
  );
  const sections = allSections.filter((section) => !isTemplateSection(section));

  const hasChanges = !isEqual(enrollments, initialEnrollments);
  const selectedSectionIds = Object.keys(enrollments);
  const currentlyEnrolledSectionIds = new Set(enabledSectionUsers.map((cu) => cu.section_id));
  const allVisibleSelected =
    sections.length > 0 && sections.every((section) => section.id in enrollments);

  const getDefaultRoleForSection = (sectionId: string): SectionRole => {
    const existing = sectionUsers.find((cu) => cu.section_id === sectionId);
    return existing?.role ?? SectionRole.STUDENT;
  };

  const setEnrollments = (next: EnrollmentRoles) => {
    setValue("enrollments", next, { shouldDirty: true });
  };

  const toggleSectionChecked = (sectionId: string) => {
    const next = { ...enrollments };
    if (sectionId in next) {
      delete next[sectionId];
    } else {
      next[sectionId] = getDefaultRoleForSection(sectionId);
    }
    setEnrollments(next);
  };

  const handleSectionSelected = (event: MouseEvent, sectionId: string) => {
    event.preventDefault();
    event.stopPropagation();
    toggleSectionChecked(sectionId);
  };

  const handleRoleChange = (sectionId: string, role: SectionRole) => {
    setEnrollments({ ...enrollments, [sectionId]: role });
  };

  const onSelectAll = () => {
    const next = { ...enrollments };
    if (allVisibleSelected) {
      sections.forEach((section) => {
        delete next[section.id];
      });
    } else {
      sections.forEach((section) => {
        if (!(section.id in next)) {
          next[section.id] = getDefaultRoleForSection(section.id);
        }
      });
    }
    setEnrollments(next);
  };

  const persistEnrollments = (currentEnrollments: EnrollmentRoles) => {
    const selectedIds = Object.keys(currentEnrollments);
    const idsToRemove = [...currentlyEnrolledSectionIds].filter(
      (id) => !(id in currentEnrollments),
    );
    const disabledSectionUsers = sectionUsers.filter((cu) => cu.is_disabled);
    const requestedSectionIds = new Set(
      selectedIds.filter((id) => !currentlyEnrolledSectionIds.has(id)),
    );

    const sectionUsersToEnable = disabledSectionUsers.filter((cu) =>
      requestedSectionIds.has(cu.section_id),
    );
    const sectionIdsToAdd = [...requestedSectionIds].filter(
      (sectionId) => !sectionUsersToEnable.some((cu) => cu.section_id === sectionId),
    );
    const sectionUsersToDisable = enabledSectionUsers.filter((cu) =>
      idsToRemove.includes(cu.section_id),
    );
    const sectionUsersToUpdateRole = enabledSectionUsers.filter(
      (cu) => cu.section_id in currentEnrollments && currentEnrollments[cu.section_id] !== cu.role,
    );

    return updateEnrollments(
      userId,
      sectionUsersToEnable.map((cu) => ({
        id: cu.id,
        role: currentEnrollments[cu.section_id],
      })),
      sectionUsersToDisable.map((cu) => cu.id),
      sectionIdsToAdd.map((sectionId) => ({
        sectionId,
        role: currentEnrollments[sectionId],
      })),
      sectionUsersToUpdateRole.map((cu) => ({
        id: cu.id,
        role: currentEnrollments[cu.section_id],
      })),
    );
  };

  const onSubmit: SubmitHandler<EnrollmentFormDataType> = async ({
    enrollments: submittedEnrollments,
  }) => {
    if (!hasChanges || !user) return;

    const idsToRemove = [...currentlyEnrolledSectionIds].filter(
      (id) => !(id in submittedEnrollments),
    );

    if (idsToRemove.length > 0) {
      const sectionNames = idsToRemove
        .sort()
        .map((sectionId) => sectionsById[sectionId]?.title || "");

      setConfirming(true);
      const confirmed = await confirmUnenrollment(getUserFullName(user), sectionNames);
      setConfirming(false);
      if (!confirmed) {
        return;
      }
    }

    if (await persistEnrollments(submittedEnrollments)) {
      onClose();
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog
      description={t("user_options_menu.manage_enrollment.description", {
        name: getUserFullName(user),
        email: user.email,
        count: enrolledCount,
      })}
      dialogPanelClassName="w-[640px]"
      open={open && !confirming}
      title={t("user_options_menu.manage_enrollment.title")}
      onClose={onClose}
    >
      <form className="mt-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <label className="min-w-0 max-w-[280px]">
              <span className="sr-only">{t("home.courses.select_course_label")}</span>
              <CourseMenu
                className="max-w-full"
                selectedCourseId={courseId}
                size="s"
                onCourseChange={(id) => setValue("courseId", id)}
              />
            </label>

            <label className="shrink-0">
              <span className="sr-only">{t("home.terms.select_term_label")}</span>
              <TermMenu
                selectedTermId={termId}
                size="s"
                onTermChange={(id) => setValue("termId", id)}
              />
            </label>
          </div>

          <LinkButton className="shrink-0" size="s" type="button" onClick={onSelectAll}>
            {t(allVisibleSelected ? "common.unselect_all" : "common.select_all")}
          </LinkButton>
        </div>

        <div className="mb-2 mt-4 max-h-[240px] overflow-y-auto rounded-lg border border-black-tint-90 p-2">
          {sections.length === 0 ? (
            <div className="text-black-tint-70">{t("copy_to_section_sub_menu.no_sections")}</div>
          ) : (
            sections.map((section) => {
              const isChecked = section.id in enrollments;
              return (
                <div className="min-w-0" key={section.id}>
                  <ListItem
                    className="min-w-0 gap-2 overflow-hidden whitespace-normal"
                    label={section.title}
                    title={section.title}
                    onClick={() => toggleSectionChecked(section.id)}
                  >
                    <Checkbox
                      checked={isChecked}
                      className="min-w-0 flex-1 overflow-hidden"
                      onContainerClick={(e) => handleSectionSelected(e, section.id)}
                    >
                      <span className="min-w-0 truncate">{section.title}</span>
                    </Checkbox>

                    <div
                      className="ml-auto shrink-0"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <Listbox
                        buttonSize="s"
                        buttonText={
                          isChecked
                            ? undefined
                            : t("user_options_menu.manage_enrollment.select_role")
                        }
                        disabled={!isChecked}
                        items={getRoleListboxItems()}
                        selectedValue={isChecked ? enrollments[section.id] : undefined}
                        onSelectionChange={(item) => handleRoleChange(section.id, item.id)}
                      />
                    </div>
                  </ListItem>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-3 text-black-tint-70">
          {t("user_options_menu.manage_enrollment.selected_count", {
            count: selectedSectionIds.length,
          })}
        </div>

        <div className="mt-8 flex justify-end gap-2">
          <SquareButton
            disabled={isSubmitting}
            kind="tertiary"
            size="m"
            title={t("common.cancel")}
            type="button"
            onClick={onClose}
          >
            {t("common.cancel")}
          </SquareButton>

          <SquareButton
            disabled={!hasChanges || isSubmitting}
            kind="accent"
            size="m"
            title={t("common.save")}
            type="submit"
          >
            {isSubmitting ? (
              <LoadingDotsIcon aria-label={t("common.submitting")} />
            ) : (
              t("common.save")
            )}
          </SquareButton>
        </div>
      </form>
    </Dialog>
  );
};
